import { GameState } from '../game/GameState.js';
import { MoveValidator } from '../game/MoveValidator.js';
import { TurnState } from '../game/TurnState.js';
import {
  sameSquare,
  type Color,
  type Move,
  type PieceKind,
  type Square,
} from '../game/types.js';
import { type ViewerRotation } from '../render/geometry.js';
import type { InteractionSnapshot } from '../render/OverlayRenderer.js';
import { PIECE_RADIUS } from '../utils/constants.js';
import type { PointerInteraction, PointerKind } from './InputHandler.js';

// Mouse cursors are precise; touch jitters more on tap, so we need a wider
// dead-zone before treating a touch as a drag (otherwise a single tap on a
// piece would often trigger a drop instead of selecting).
const MOUSE_DRAG_THRESHOLD_PX = 6;
const TOUCH_DRAG_THRESHOLD_PX = 14;
// Lift the drag ghost above the finger so the user can see what's underneath
// the piece they're moving. Mouse cursors don't occlude anything so they
// render the ghost in-place.
const TOUCH_DRAG_LIFT_PX = PIECE_RADIUS * 1.6;

export interface MoveSelectorOptions {
  readonly getState: () => GameState;
  readonly getRotation: () => ViewerRotation;
  /** True while a move animation is playing — input is dropped on the floor. */
  readonly isInputBlocked: () => boolean;
  /** Called when the user has produced a fully-committed Move. */
  readonly onMove: (move: Move) => void;
}

interface DragState {
  readonly fromSquare: Square;
  readonly color: Color;
  readonly downAt: { x: number; y: number };
  readonly pointer: PointerKind;
  position: { x: number; y: number };
  active: boolean; // becomes true once threshold is exceeded
}

/**
 * Click + drag interaction state machine. Translates pointer events into
 * `TurnState` operations and emits a Move once the chain completes.
 *
 * Interaction model:
 *   - Click an own piece → select. Click another own piece → switch.
 *   - Click selected piece again → deselect.
 *   - Click empty non-destination → deselect (only when chain hasn't started).
 *   - Drag from own piece → drop on a valid destination = step.
 *   - During a chain the only legal targets are valid next-step squares; the
 *     user cannot abandon a chain partway through (Russian rule: capture must
 *     be completed).
 */
export class MoveSelector {
  private readonly turn: TurnState;
  private hovered: Square | null = null;
  private drag: DragState | null = null;
  // True for the lifetime of one down→up cycle when pointerdown already
  // performed selection: without this, a single tap (down selects, up sees
  // the selected square and toggles it off) would never settle on 'selected'.
  private downChangedSelection = false;

  constructor(
    state: GameState,
    validator: MoveValidator,
    private readonly opts: MoveSelectorOptions,
  ) {
    this.turn = new TurnState(state, validator);
  }

  /** Rebind to a new GameState — call after applying a committed move. */
  resetTo(state: GameState): void {
    this.turn.reset(state);
    this.hovered = null;
    this.drag = null;
  }

  get turnState(): TurnState {
    return this.turn;
  }

  snapshot(timeMs: number): InteractionSnapshot {
    const selected = this.turn.currentSquare;
    const dragActive = !!this.drag?.active;
    const draggedKind: PieceKind | null = dragActive
      ? this.turn.currentPieceKind() ?? this.kindAtOrigin()
      : null;
    return {
      currentPlayer: this.opts.getState().currentPlayer,
      hovered: this.hovered,
      selected,
      dragPosition: dragActive && this.drag ? this.ghostPosition(this.drag) : null,
      draggedKind,
      validDestinations: this.turn.nextDestinations(),
      captureTargets: this.hovered ? this.turn.captureTargetsFor(this.hovered) : [],
      mandatorySources: this.turn.mandatorySources(),
      timeMs,
    };
  }

  private ghostPosition(drag: DragState): { x: number; y: number } {
    if (drag.pointer !== 'touch') return drag.position;
    return { x: drag.position.x, y: drag.position.y - TOUCH_DRAG_LIFT_PX };
  }

  handle(event: PointerInteraction): void {
    if (this.opts.isInputBlocked()) {
      this.hovered = null;
      this.drag = null;
      this.downChangedSelection = false;
      return;
    }
    switch (event.kind) {
      case 'move':
        this.handleMove(event);
        return;
      case 'down':
        this.handleDown(event);
        return;
      case 'up':
        this.handleUp(event);
        return;
      case 'leave':
        // Keep selection on leave — just clear hover/drag. Lets the user move
        // off-canvas and back without losing the chain in progress.
        this.hovered = null;
        this.drag = null;
        this.downChangedSelection = false;
        return;
      case 'cancel':
        this.hovered = null;
        this.drag = null;
        this.downChangedSelection = false;
        return;
    }
  }

  private handleMove(event: PointerInteraction): void {
    this.hovered = event.square;
    if (this.drag) {
      this.drag.position = { x: event.canvasX, y: event.canvasY };
      if (!this.drag.active) {
        const dx = event.canvasX - this.drag.downAt.x;
        const dy = event.canvasY - this.drag.downAt.y;
        const threshold =
          this.drag.pointer === 'touch' ? TOUCH_DRAG_THRESHOLD_PX : MOUSE_DRAG_THRESHOLD_PX;
        if (Math.hypot(dx, dy) >= threshold) {
          this.drag.active = true;
        }
      }
    }
  }

  private handleDown(event: PointerInteraction): void {
    this.hovered = event.square;
    this.downChangedSelection = false;
    if (!event.square) return;
    const sq = event.square;
    const state = this.opts.getState();

    // Mid-chain: only the current chain square is a valid drag origin.
    if (this.turn.phase === 'chaining') {
      const here = this.turn.currentSquare;
      if (here && sameSquare(here, sq)) {
        const originPiece = this.turn.origin
          ? state.pieceAt(this.turn.origin)
          : null;
        if (originPiece) {
          this.drag = {
            fromSquare: sq,
            color: originPiece.color,
            downAt: { x: event.canvasX, y: event.canvasY },
            pointer: event.pointer,
            position: { x: event.canvasX, y: event.canvasY },
            active: false,
          };
        }
      }
      return;
    }

    const piece = state.pieceAt(sq);
    if (piece && piece.color === state.currentPlayer && this.turn.canSelect(sq)) {
      const alreadySelected = this.turn.origin && sameSquare(this.turn.origin, sq);
      if (!alreadySelected) {
        this.turn.deselect();
        this.turn.select(sq);
        this.downChangedSelection = true;
      }
      this.drag = {
        fromSquare: sq,
        color: piece.color,
        downAt: { x: event.canvasX, y: event.canvasY },
        pointer: event.pointer,
        position: { x: event.canvasX, y: event.canvasY },
        active: false,
      };
    }
  }

  private handleUp(event: PointerInteraction): void {
    const target = event.square;
    const wasDragging = !!this.drag?.active;
    const downChangedSelection = this.downChangedSelection;
    this.drag = null;
    this.downChangedSelection = false;

    if (wasDragging) {
      // Drop semantics: only a drop on a valid next-step square commits.
      if (!target) return;
      const result = this.turn.step(target);
      if (result.kind === 'complete') this.commit(result.move);
      return;
    }

    // The pointerdown that paired with this up may have already done the
    // selection (single tap on an own piece). Don't re-toggle in that case.
    if (downChangedSelection) return;

    if (!target) {
      // Click released off-board. Pre-chain: drop selection.
      if (this.turn.phase === 'selected') this.turn.deselect();
      return;
    }

    const state = this.opts.getState();
    const piece = state.pieceAt(target);
    const ownPiece = piece && piece.color === state.currentPlayer;

    if (this.turn.phase === 'idle') {
      if (ownPiece && this.turn.canSelect(target)) this.turn.select(target);
      return;
    }

    if (this.turn.phase === 'selected') {
      const origin = this.turn.origin;
      // Second click on the already-selected square toggles it off.
      if (origin && sameSquare(origin, target)) {
        this.turn.deselect();
        return;
      }
      // Switch pieces when clicking another own piece that has moves.
      if (ownPiece && this.turn.canSelect(target)) {
        this.turn.deselect();
        this.turn.select(target);
        return;
      }
      // Otherwise: try to step. Invalid clicks on an empty non-destination
      // deselect (matches the "click empty to deselect" UX).
      const result = this.turn.step(target);
      if (result.kind === 'complete') {
        this.commit(result.move);
      } else if (result.kind === 'invalid' && !piece) {
        this.turn.deselect();
      }
      return;
    }

    if (this.turn.phase === 'chaining') {
      // Only valid destinations are accepted; other clicks are no-ops.
      const result = this.turn.step(target);
      if (result.kind === 'complete') this.commit(result.move);
      return;
    }
  }

  private commit(move: Move): void {
    this.drag = null;
    this.hovered = null;
    this.opts.onMove(move);
  }

  private kindAtOrigin(): PieceKind | null {
    const origin = this.turn.origin;
    if (!origin) return null;
    return this.opts.getState().pieceAt(origin)?.kind ?? null;
  }
}
